/**
 * Context that bridges SaaS-specific functionality into the shared
 * Cascade editor components (which can't import from `../saas/`).
 *
 * Provides the Supabase client, scene mutation callbacks, and AI chat
 * config to editor panels like the Threads sidebar.
 */

import type {SupabaseClient} from '@supabase/supabase-js';
import type {AudioClipConfig} from '@cascade/core';
import {createContext, useContext, type ReactNode} from 'react';
import type {Scene} from '../db';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SaaSEditorContextValue {
  /** Supabase client for direct DB operations. */
  supabase: SupabaseClient;
  /** Current project ID. */
  projectId: string;
  /** Current scenes loaded from Supabase. */
  scenes: Scene[];
  /** Canvas dimensions for AI prompt context. */
  width: number;
  height: number;
  /** Frames per second from project settings. */
  fps: number;
  /** Cached audio analyses keyed by audio source URL. */
  audioAnalyses: Record<string, unknown>;
  /** Audio clips from project settings. */
  audioClips: AudioClipConfig[];
  /** Callback after a new scene is created (triggers reload). */
  onSceneCreated: () => void;
  /** Callback to remove a scene by index. Returns true on success. */
  onSceneRemoved: (sceneIndex: number) => Promise<boolean>;
  /** Callback when audio analysis is saved -- updates the in-memory cache. */
  onAudioAnalysisUpdated: (audioSrc: string, analysis: unknown) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SaaSEditorContext = createContext<SaaSEditorContextValue | null>(null);

/**
 * Hook to access SaaS editor context.
 * Returns null when running outside SaaS mode (e.g. local dev server).
 */
export function useSaaSEditor(): SaaSEditorContextValue | null {
  return useContext(SaaSEditorContext);
}

/**
 * Provider that wraps the editor when running in SaaS mode.
 */
export function SaaSEditorProvider({
  value,
  children,
}: {
  value: SaaSEditorContextValue;
  children: ReactNode;
}) {
  return (
    <SaaSEditorContext.Provider value={value}>
      {children}
    </SaaSEditorContext.Provider>
  );
}
